import React, {
    useState,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { Controlled as CodeMirror } from 'react-codemirror2';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';
import { Buffer } from 'buffer';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
// import Select from '@mui/material/Select';
// import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import ButtonGroup from '@mui/material/ButtonGroup';
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import Filter from './filter';
import Validator, { useValidate, checkValidity } from '../validator';

import defaultConfig from '../../data/eexportd';
import SystemService from '../../data/system-service';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    timezone: {
        width: '120px',
    },
    hideFileInput: {
        display: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    certBox: {
        marginTop: theme.spacing(2),
        padding: '5px 8px',
        border: `1px solid ${theme.palette.primary.main}`,
        background: theme.styled.paper.background,
        color: theme.palette.text.primary,
    },
    codemirrorBox: {
        display: 'block',
        width: '100%',
    },
    codeContainer: {
        height: '120px',
        marginBottom: '-8px',
        '& .react-codemirror2': {
            height: '100%',
        },
        '& .CodeMirror': {
            height: '100%',
        }
    },
}));

const FORMAT_STRINGS = ['$health_check$', '$health_service$', '$health_host$', '$health_message$', '$health_type$', '$health_state$',
    '$health_uuid$', '$webhook_id$', '$webhook_severity$', '$timestamp_epoch$'];

const Webhook = forwardRef((props, ref) => {
    const classes = useStyles();
    const theme = useTheme();
    const [, setSystemConstants] = useState({
        facilities: [],
        severities: [],
        timezones: [],
    });
    const filterRef = useRef(null);
    const [original, setOriginal] = useState(_cloneDeep({
        ...defaultConfig,
        ...props.data
    }));
    const [data, setData] = useState(_cloneDeep({
        ...defaultConfig,
        ...props.data
    }));
    const [changes, setChanges] = useState(false);
    const dataForm = useValidate({});
    const [formatRef, setFormatRef] = useState(null);
    const [basicuser, setBasicuser] = useState('');
    const [basicpass, setBasicpass] = useState('');
    const [istls, setIstls] = useState(false);

    useImperativeHandle(ref, () => ({
        getData: () => {
            const f = filterRef.current.getFilter();
            return {
                ...data,
                filterconf: f,
            };
        },
    }));

    useEffect(() => {
        SystemService.getConstants().then((res) => {
            setSystemConstants(res);
        });
    }, []);

    useEffect(() => {
        setChanges(!_isEqual(data, original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (props.data) {
            const d = { ...props.data };

            // for now force only health alerts
            if (d.event_types) {
                d.event_types = ['health_alert'];
            }
            // set initial url if empty on load
            if (d.webhook.url === '') {
                d.webhook.url = 'https://127.0.0.1';
            }

            setOriginal(_cloneDeep({ ...defaultConfig, ...d }));
            setData(_cloneDeep({ ...defaultConfig, ...d }));
        }
    }, [props]);

    useEffect(() => {
        if (props.data && props.data.webhook) {
            const d = { ...props.data.webhook };
            if (d.auth && d.auth !== '') {
                const dAuth = atob(d.auth);
                const toks = dAuth.split(':');
                if (toks.length === 2) {
                    setBasicuser(toks[0]);
                    setBasicpass(toks[1]);
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUrl = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.webhook.url = e.target.value;
        // use URL to determine TLS-ness
        setIstls(data.webhook.url.substring(0, 5).toLowerCase() === 'https');
        setData(d);
    };

    const handleProxy = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.webhook.proxy = e.target.value;
        setData(d);
    };

    const handleUser = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        setBasicuser(e.target.value);
        // eslint-disable-next-line node/no-deprecated-api
        d.webhook.auth = new Buffer(basicuser + ':' + basicpass).toString('base64');
        setData(d);
    };

    const handlePass = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        setBasicpass(e.target.value);
        // eslint-disable-next-line node/no-deprecated-api
        d.webhook.auth = new Buffer(basicuser + ':' + basicpass).toString('base64');
        setData(d);
    };

    const handleSetFormat = (format) => {
        const d = { ...data };
        if (format === 'empty') {
            d.webhook.format = '';
        } else {
            // Move these into system constants when get more
            d.webhook.format = 'source_id:$health_uuid$|external_id:$webhook_id$|manager:Manager Name|source:$health_host$|' +
                'class:application|agent_location:My Agent Location|type:$health_type$|severity:$webhook_severity$|' +
                'description:$health_message$|assignment_group:My Assignment Group|agent_time:$timestamp_epoch$|' +
                'check:$health_check$|service:$health_service$';
        }
        setData(d);
    };

    const handleFormat = (e) => {
        const d = { ...data };
        d.webhook.format = e;
        setData(d);
    };

    const handleValidateSslHost = (e) => {
        const d = { ...data };
        d.webhook.validate_ssl_host = e.currentTarget.checked;
        setData(d);
    };

    const handleValidateSslPeer = (e) => {
        const d = { ...data };
        d.webhook.validate_ssl_peer = e.currentTarget.checked;
        setData(d);
    };

    const handleCACert = (e) => {
        const d = { ...data };
        d.tls_cert.enable = e.currentTarget.checked;
        setData(d);
    };

    const fileHandler = (file) => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
        });
    };

    const handleTLSContents = (e) => {
        const f = e.target.files[0];
        fileHandler(e.target.files[0]).then(content => {
            const d = { ...data };
            d.tls_cert.contents = content;
            d.tls_cert.file_name = f.name;
            setData(d);
        }).catch(err => console.error(err));
    };

    const handleTLSClientKey = (e) => {
        const f = e.target.files[0];
        fileHandler(e.target.files[0]).then(content => {
            const d = { ...data };
            d.tls_client_key.contents = content;
            d.tls_client_key.file_name = f.name;
            setData(d);
        }).catch(err => console.error(err));
    };

    const handleTLSClientCert = (e) => {
        const f = e.target.files[0];
        fileHandler(e.target.files[0]).then(content => {
            const d = { ...data };
            d.tls_client_cert.contents = content;
            d.tls_client_cert.file_name = f.name;
            setData(d);
        }).catch(err => console.error(err));
    };

    const handleFIPS = (e) => {
        const d = { ...data };
        d.tls_fips = e.currentTarget.checked;
        setData(d);
    };

    const handleClientAuth = (e) => {
        const d = { ...data };
        d.tls_client_auth = e.currentTarget.checked;
        d.tls_client_cert.enable = e.currentTarget.checked;
        d.tls_client_key.enable = e.currentTarget.checked;
        setData(d);
    };

    const handleVerify = (k, i, close) => {
        props.onVerify(k, i, close);
    };

    const handleCancel = (k, i) => {
        props.onCancel(k, i);
    };

    const handleEventTypeHealthAlerts = (e) => {
        const d = { ...data };
        // For now, disallow other event types
        if (e.currentTarget.checked) {
            d.event_types = ['health_alert'];
            // d.event_types.push('health_alert');
        } else {
            d.event_types = [];
            // d.event_types.splice(d.event_types.indexOf('health_alert'), 1);
        }
        setData(d);
    };

    const codemirrorOptions = {
        theme: theme.codeMirror.theme,
        lineWrapping: true,
    };

    const handleAddFormatString = (e) => {
        const d = { ...data };
        const cp = formatRef.getCursor().ch;

        d.webhook.format = [
            data.webhook.format.slice(0, cp),
            e.currentTarget.name,
            data.webhook.format.slice(cp),
        ].join('');

        setData(d);
    };

    const handleUpdateFilter = (filter) => {
        setData({
            ...data,
            filterconf: filter,
        });
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box>
                    <Box display="inline-block" pr={5}>Event Type(s):</Box>
                    <FormControlLabel
                        label="Health Alerts"
                        onChange={(e) => { handleEventTypeHealthAlerts(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('health_alert') !== -1} />}
                    />
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>URL</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="url"
                            errorText="Must be a valid URL"
                            fullWidth
                            value={data.webhook && data.webhook.url}
                            onChange={handleUrl}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Proxy (optional)</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="url"
                            errorText="Must be a valid URL"
                            fullWidth
                            allowEmpty
                            value={data.webhook && data.webhook.proxy}
                            onChange={handleProxy}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Basic Auth User (optional)</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="length"
                            inputProps={{
                                min: 0,
                                max: 500,
                            }}
                            fullWidth
                            allowEmpty
                            value={basicuser}
                            onChange={handleUser}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Basic Auth Pass (optional)</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="length"
                            inputProps={{
                                min: 0,
                                max: 500,
                            }}
                            type="password"
                            fullWidth
                            allowEmpty
                            value={basicpass}
                            onChange={handlePass}
                        />
                    </FormControl>
                </Box>
            </Grid>
            <Grid item container xs={6} alignContent="flex-start" spacing={2}>
                <Grid item xs={12}>
                    <Box mt={3}>
                        <FormControlLabel
                            labelPlacement="start"
                            label="Validate SSL Peer"
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !istls}
                            control={
                                <Checkbox
                                    checked={data.webhook && data.webhook.validate_ssl_peer}
                                    name=""
                                    onChange={handleValidateSslPeer}
                                />
                            }
                        />
                    </Box>
                    <Box mt={3}>
                        <FormControlLabel
                            labelPlacement="start"
                            label="Validate SSL Host"
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !istls}
                            control={
                                <Checkbox
                                    checked={data.webhook && data.webhook.validate_ssl_host}
                                    name=""
                                    onChange={handleValidateSslHost}
                                />
                            }
                        />
                    </Box>
                    <Box>
                        <FormControlLabel
                            labelPlacement="start"
                            label="FIPS"
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !istls}
                            control={
                                <Checkbox
                                    checked={data.tls_fips}
                                    name=""
                                    onChange={handleFIPS}
                                />
                            }
                        />
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box mt={3}>
                        <FormControlLabel
                            labelPlacement="start"
                            label="CA Cert"
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !istls}
                            control={
                                <Checkbox
                                    checked={data.tls_cert.enable}
                                    name=""
                                    onChange={handleCACert}
                                />
                            }
                        />
                    </Box>
                    <Box mt={3}>
                        <input
                            id={'webhook-cert-file-' + props.index}
                            className={classes.hideFileInput}
                            type="file"
                            onChange={handleTLSContents}
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_cert.enable || !istls}
                        />
                        <label htmlFor={'webhook-cert-file-' + props.index}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="upload cert"
                                component="span"
                                disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_cert.enable || !istls}
                            >
                                Upload Cert
                            </Button>
                        </label>
                    </Box>
                    <Box className={classes.certBox} display={data.tls_cert.file_name ? 'flex' : 'none'}>
                        {data.tls_cert.file_name ? 'Filename: ' : ''} {data.tls_cert.file_name}
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box mt={3}>
                        <FormControlLabel
                            labelPlacement="start"
                            label="TLS Client Auth"
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !istls}
                            control={
                                <Checkbox
                                    checked={data.tls_client_auth}
                                    name="tls_client_auth"
                                    onChange={handleClientAuth}
                                />
                            }
                        />
                    </Box>
                    <Box mt={3}>
                        <input
                            id={'webhook-client-key-' + props.index}
                            className={classes.hideFileInput}
                            type="file"
                            onChange={handleTLSClientKey}
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_client_auth || !istls}
                        />
                        <label htmlFor={'webhook-client-key-' + props.index}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="upload key"
                                component="span"
                                disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_client_auth || !istls}
                            >
                                Upload Key
                            </Button>
                        </label>
                    </Box>
                    <Box className={classes.certBox} display={data.tls_client_key && data.tls_client_key.file_name ? 'flex' : 'none'}>
                        {data.tls_client_key && data.tls_client_key.file_name ? 'Filename: ' : ''} {data.tls_client_key && data.tls_client_key.file_name}
                    </Box>
                    <Box mt={3}>
                        <input
                            id={'webhook-client-cert-' + props.index}
                            className={classes.hideFileInput}
                            type="file"
                            onChange={handleTLSClientCert}
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_client_auth || !istls}
                        />
                        <label htmlFor={'webhook-client-cert-' + props.index}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="upload cert"
                                component="span"
                                disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_client_auth || !istls}
                            >
                                Upload Cert
                            </Button>
                        </label>
                    </Box>
                    <Box className={classes.certBox} display={data.tls_client_cert && data.tls_client_cert.file_name ? 'flex' : 'none'}>
                        {data.tls_client_cert && data.tls_client_cert.file_name ? 'Filename: ' : ''} {data.tls_client_cert && data.tls_client_cert.file_name}
                    </Box>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Box className={classes.codeContainer} width="100%">
                    <CodeMirror
                        readOnly={!props.perms.write}
                        editorDidMount={(e) => { setFormatRef(e) }}
                        value={data.webhook.format}
                        options={codemirrorOptions}
                        onBeforeChange={(c, e, value) => { handleFormat(value) }}
                        lineWrapping={true}
                    />
                </Box>

                {/* <TextField
                    disabled={(!props.perms.write || props.policySource === 'mc2')}
                    label="Format"
                    fullWidth
                    multiline
                    value={data.syslog.format}
                    inputRef={syslogRef}
                    onChange={handleFormat}
                /> */}
                <Box mt={1}>
                    <Accordion>
                        <AccordionSummary>
                            <Box display="flex" justifyContent="flex-end">
                                Format Strings
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                {FORMAT_STRINGS.map((fs, i) => {
                                    return (
                                        <Button
                                            key={fs + '-' + i}
                                            name={fs}
                                            color="primary"
                                            onClick={handleAddFormatString}
                                            style={{ textTransform: 'none' }}
                                        >{fs}</Button>
                                    );
                                })}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Typography>Apply format preset:</Typography>
                <ButtonGroup disabled={(!props.perms.write || props.policySource === 'mc2')}>
                    <Button variant="contained" color="primary" aria-label="conn" onClick={() => { handleSetFormat('health') }}>Health</Button>
                    <Button variant="contained" color="primary" aria-label="empty" onClick={() => { handleSetFormat('empty') }}>Empty</Button>
                </ButtonGroup>
            </Grid>
            <Filter
                filter={data.filterconf}
                ref={filterRef}
                onUpdate={handleUpdateFilter}
                onVerify={() => { handleVerify(props.name, props.index, false) }}
                perms={props.perms}
            />
            {(props.perms.write || props.policySource !== 'mc2') && <Grid item xs={12}>
                <Box mt={2}>
                    <Button
                        className={classes.buttonRight}
                        variant="contained"
                        color="primary"
                        aria-label="save"
                        onClick={() => { handleVerify(props.name, props.index, true) }}
                        disabled={!(checkValidity(dataForm.get()))}
                    >Verify</Button>
                    <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={() => { handleCancel(props.name, props.index) }}>Cancel</Button>
                </Box>
            </Grid>}
        </Grid>
    );
});

Webhook.propTypes = {
    data: PropTypes.object.isRequired,
    policySource: PropTypes.string,
    name: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    onCancel: PropTypes.func.isRequired,
    onVerify: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onUpdateForm: PropTypes.func,
    perms: PropTypes.object,
};

export default Webhook;
